import type { TZDate } from '@date-fns/tz';
import { isBefore } from 'date-fns';
import type { Dispatch } from 'react';
import type { StoreApi } from 'zustand';

import type { CalendarStrictValueType } from '../../../../forms/calendar/Calendar.types.js';
import { CALENDAR_ERROR_MESSAGES } from '../../../../forms/calendar/calendarMessages.js';
import type { CalendarType } from '../../../../forms/calendar/CalendarRoot.js';
import type { FormFieldMessagesReducerAction } from '../../../../forms/forms-core/state/formfield-messages-state-reducer.js';
import { getTimeValueFromSimpleValue } from '../../../../forms/forms-core/time/helpers.js';
import type { TimeValue } from '../../../types/time.js';
import { isFullTimeframe, isTimeframe } from '../../helpers.js';
import { validate } from '../../validation.js';
import type {
  CalendarTimeframeState,
  CalendarTimeframeStoreActions,
} from '../calendar.types.js';

/**
 * Gets the timeframe from the calendar selection.
 * @internal
 */
export function getTimeframeFromCalendarSelection(
  inputValue: TZDate | null,
  state: CalendarTimeframeState,
): { from: TimeValue | null; to: TimeValue | null } | null {
  if (!isTimeframe(state.calendarValue, 'range')) {
    // early exit to ensure type safety for the timeframe operations
    return null;
  }

  if (inputValue === null) {
    return null;
  }

  const stringDate = inputValue.toISOString();
  const calendarRangeValue = state.calendarValue as
    | { from: TimeValue | null; to: TimeValue | null }
    | null
    | undefined;
  const { from, to } = calendarRangeValue ?? {};

  let position: 'from' | 'to' = 'from';
  let newTimeframe: { from: TimeValue | null; to: TimeValue | null } = {
    from: null,
    to: null,
  };

  if (from && !to) {
    const isToBeforeFrom = isBefore(stringDate, from.absoluteDate);

    if (!isToBeforeFrom) {
      newTimeframe = {
        from,
        to: null,
      };
      position = 'to';
    }

    // we need to swap from and to
    if (isToBeforeFrom) {
      newTimeframe = {
        from: null,
        to: getTimeValueFromSimpleValue(
          from.absoluteDate,
          state.timeOffset,
          'to',
          undefined,
          state.min,
          state.max,
          state.precision,
        ),
      };
    }
  }

  const newTimeValue = getTimeValueFromSimpleValue(
    stringDate,
    state.timeOffset,
    position,
    undefined,
    state.min,
    state.max,
    state.precision,
  );

  newTimeframe[position] = newTimeValue;

  return newTimeframe;
}

/**
 * Creates actions for the Calendar store using the slice action handlers.
 * @internal
 */
export function getCalendarActions<Type extends CalendarType>(
  isControlled: boolean,
  type?: CalendarType,
  formFieldMessagesDispatch?: Dispatch<FormFieldMessagesReducerAction>,
  onChangeProp?: (date: CalendarStrictValueType<Type>) => void,
): (
  set: StoreApi<CalendarTimeframeState>['setState'],
) => CalendarTimeframeStoreActions {
  return (set) => ({
    setCalendarValue: (inputValue) => {
      set((state) => {
        if (type === 'range') {
          const newTimeframe = getTimeframeFromCalendarSelection(
            inputValue,
            state,
          );

          if (isFullTimeframe(newTimeframe) || newTimeframe === null) {
            (
              onChangeProp as (date: CalendarStrictValueType<'range'>) => void
            )?.(newTimeframe);

            if (isControlled) {
              return state;
            }
          }

          const calendarError = validate(newTimeframe, state.min, state.max, {
            formMessageDispatch: formFieldMessagesDispatch,
            formerError: state.calendarError,
            precision: state.precision,
            showError: false,
            checkOrder: true,
            ref: state.calendarRef,
            valueMode: 'timeframeSelector',
            errorMessages: CALENDAR_ERROR_MESSAGES,
          });

          return {
            calendarValue: newTimeframe,
            calendarError,
          };
        }

        const newTimeValue =
          getTimeValueFromSimpleValue(
            inputValue,
            state.timeOffset,
            undefined,
            undefined,
            state.min,
            state.max,
            state.precision,
          ) ?? null;

        (onChangeProp as (date: CalendarStrictValueType<'single'>) => void)?.(
          newTimeValue?.value ?? null,
        );

        if (isControlled) {
          return state;
        }

        const calendarError = validate(newTimeValue, state.min, state.max, {
          formMessageDispatch: formFieldMessagesDispatch,
          formerError: state.calendarError,
          precision: state.precision,
          showError: false,
          checkOrder: true,
          ref: state.calendarRef,
          valueMode: 'single',
          errorMessages: CALENDAR_ERROR_MESSAGES,
        });

        return { calendarValue: newTimeValue, calendarError };
      });
    },
  });
}
